import React, { useState, useMemo } from 'react';
import { BarChart3, TrendingUp, Users, Building, Package, DollarSign, Clock, Target, Activity, MapPin, Calendar, Star } from 'lucide-react';
import ViewLayout from '../ui/ViewLayout';

const AnalyticsView = ({ 
  currentUser,
  props = [], 
  reservations = [], 
  transferRequests = [],
  organizations = []
}) => {
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, 1y
  const [activeTab, setActiveTab] = useState('inventory'); // inventory, borrowers, warehouses

  // Filter data based on current user's organization (our inventory)
  const ourProps = props.filter(prop => prop.owner === currentUser.organization);
  const ourReservations = reservations.filter(res => res.target_org?.name === currentUser.organization);
  const borrowerReservations = reservations.filter(res => res.requested_by_org !== currentUser.organization);

  // Date filtering helper
  const getDateCutoff = (range) => {
    const now = new Date();
    switch(range) {
      case '7d': return new Date(now - 7 * 24 * 60 * 60 * 1000);
      case '30d': return new Date(now - 30 * 24 * 60 * 60 * 1000);
      case '90d': return new Date(now - 90 * 24 * 60 * 60 * 1000);
      case '1y': return new Date(now - 365 * 24 * 60 * 60 * 1000);
      default: return new Date(now - 30 * 24 * 60 * 60 * 1000);
    }
  };

  const dateCutoff = getDateCutoff(timeRange);
  const recentReservations = ourReservations.filter(res => new Date(res.created_at) >= dateCutoff);

  // Calculate comprehensive analytics
  const analytics = useMemo(() => {
    // 1. OUR INVENTORY ANALYTICS
    const inventoryStats = {
      totalProps: ourProps.length,
      availableProps: ourProps.filter(p => p.status === 'available').length,
      checkedOutProps: ourProps.filter(p => p.status === 'checked_out').length,
      utilizationRate: ourProps.length > 0 ? Math.round((ourProps.filter(p => p.status === 'checked_out').length / ourProps.length) * 100) : 0,
      
      // Category breakdown
      categoryBreakdown: ourProps.reduce((acc, prop) => {
        acc[prop.category] = (acc[prop.category] || 0) + 1;
        return acc;
      }, {}),
      
      // Most popular props (by checkout frequency)
      popularProps: ourProps
        .map(prop => ({
          ...prop,
          checkoutCount: recentReservations.filter(res => 
            res.reservation_items?.some(item => item.prop_id === prop.id)
          ).length
        }))
        .sort((a, b) => b.checkoutCount - a.checkoutCount)
        .slice(0, 10),
      
      // Financial metrics
      totalValue: ourProps.reduce((sum, prop) => sum + (prop.estimated_value || 0), 0),
      transferRevenue: recentReservations.reduce((sum, res) => 
        sum + (res.reservation_items?.reduce((itemSum, item) => 
          itemSum + (ourProps.find(p => p.id === item.prop_id)?.transfer_cost || 0), 0) || 0), 0),
    };

    // 2. BORROWER ANALYTICS
    const borrowerStats = {
      totalBorrowers: [...new Set(recentReservations.map(res => res.requesting_org?.name))].length,
      totalReservations: recentReservations.length,
      approvalRate: recentReservations.length > 0 ? 
        Math.round((recentReservations.filter(res => res.status === 'approved' || res.status === 'completed').length / recentReservations.length) * 100) : 0,
      
      // Top borrowing organizations
      topBorrowers: Object.entries(
        recentReservations.reduce((acc, res) => {
          const org = res.requesting_org?.name || 'Unknown';
          acc[org] = (acc[org] || 0) + 1;
          return acc;
        }, {})
      )
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([org, count]) => ({ organization: org, reservations: count })),
      
      // Average checkout duration
      avgCheckoutDuration: (() => {
        const completedReservations = recentReservations.filter(res => res.status === 'completed');
        if (completedReservations.length === 0) return 0;
        const totalDays = completedReservations.reduce((sum, res) => {
          const start = new Date(res.approved_pickup_date || res.created_at);
          const end = new Date(res.expected_return_date);
          return sum + Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
        }, 0);
        return Math.round(totalDays / completedReservations.length);
      })(),
    };

    // 3. WAREHOUSE RELATIONSHIPS ANALYTICS
    const warehouseStats = {
      totalPartners: organizations.filter(org => org.name !== currentUser.organization).length,
      
      // Organizations we borrow from most
      borrowingPartners: Object.entries(
        transferRequests
          .filter(req => req.to_org?.name === currentUser.organization)
          .reduce((acc, req) => {
            const org = req.from_org?.name || 'Unknown';
            acc[org] = (acc[org] || 0) + 1;
            return acc;
          }, {})
      )
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([org, count]) => ({ organization: org, transfers: count })),
      
      // Organizations that borrow from us most
      lendingPartners: Object.entries(
        recentReservations.reduce((acc, res) => {
          const org = res.requesting_org?.name || 'Unknown';
          acc[org] = (acc[org] || 0) + 1;
          return acc;
        }, {})
      )
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([org, count]) => ({ organization: org, loans: count })),
    };

    return { inventoryStats, borrowerStats, warehouseStats };
  }, [ourProps, recentReservations, transferRequests, organizations, currentUser.organization]);

  // Overall stats for header
  const overallStats = [
    <ViewLayout.Stat 
      title="Total Props" 
      value={analytics.inventoryStats.totalProps} 
      icon={Package} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Utilization Rate" 
      value={`${analytics.inventoryStats.utilizationRate}%`} 
      icon={TrendingUp} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Active Borrowers" 
      value={analytics.borrowerStats.totalBorrowers} 
      icon={Users} 
      color="purple" 
    />,
    <ViewLayout.Stat 
      title="Partner Warehouses" 
      value={analytics.warehouseStats.totalPartners} 
      icon={Building} 
      color="orange" 
    />
  ];

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' }
  ];

  const tabOptions = [
    { id: 'inventory', label: 'Our Inventory', icon: Package },
    { id: 'borrowers', label: 'Borrower Analytics', icon: Users },
    { id: 'warehouses', label: 'Warehouse Partners', icon: Building }
  ];

  return (
    <ViewLayout
      title="Analytics Dashboard"
      subtitle={`${currentUser.organization} - Performance Insights`}
      stats={overallStats}
    >
      <div className="space-y-6">
        {/* Time Range and Tab Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex gap-2">
            {tabOptions.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
          
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {timeRangeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* INVENTORY ANALYTICS TAB */}
        {activeTab === 'inventory' && (
          <div className="space-y-6">
            {/* Key Inventory Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Target className="h-8 w-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold text-green-600">{analytics.inventoryStats.utilizationRate}%</div>
                    <div className="text-sm text-gray-600">Utilization Rate</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-8 w-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold text-blue-600">${analytics.inventoryStats.totalValue.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Total Inventory Value</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold text-purple-600">${analytics.inventoryStats.transferRevenue}</div>
                    <div className="text-sm text-gray-600">Transfer Revenue ({timeRange})</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Activity className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold text-orange-600">{recentReservations.length}</div>
                    <div className="text-sm text-gray-600">Recent Reservations</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Inventory by Category
                </h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(analytics.inventoryStats.categoryBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .map(([category, count]) => (
                    <div key={category} className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{count}</div>
                      <div className="text-sm text-gray-600">{category}</div>
                      <div className="text-xs text-gray-500">
                        {Math.round((count / analytics.inventoryStats.totalProps) * 100)}% of inventory
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Most Popular Props */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-600" />
                  Most Popular Props ({timeRange})
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {analytics.inventoryStats.popularProps.slice(0, 8).map((prop, index) => (
                    <div key={prop.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{prop.name}</div>
                          <div className="text-sm text-gray-600">{prop.category}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-blue-600">{prop.checkoutCount}</div>
                        <div className="text-xs text-gray-500">checkouts</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BORROWER ANALYTICS TAB */}
        {activeTab === 'borrowers' && (
          <div className="space-y-6">
            {/* Key Borrower Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{analytics.borrowerStats.totalBorrowers}</div>
                    <div className="text-sm text-gray-600">Active Borrowers</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold text-green-600">{analytics.borrowerStats.totalReservations}</div>
                    <div className="text-sm text-gray-600">Total Reservations</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Target className="h-8 w-8 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{analytics.borrowerStats.approvalRate}%</div>
                    <div className="text-sm text-gray-600">Approval Rate</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold text-orange-600">{analytics.borrowerStats.avgCheckoutDuration}</div>
                    <div className="text-sm text-gray-600">Avg Checkout Days</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Borrowing Organizations */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Top Borrowing Organizations ({timeRange})
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {analytics.borrowerStats.topBorrowers.map((borrower, index) => (
                    <div key={borrower.organization} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{borrower.organization}</div>
                          <div className="text-sm text-gray-600">Active borrower</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-blue-600">{borrower.reservations}</div>
                        <div className="text-xs text-gray-500">reservations</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Borrower Insights */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">💡 Borrower Insights</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <div>• Your approval rate of {analytics.borrowerStats.approvalRate}% shows {analytics.borrowerStats.approvalRate >= 80 ? 'excellent' : analytics.borrowerStats.approvalRate >= 60 ? 'good' : 'room for improvement in'} request management</div>
                <div>• Average checkout duration of {analytics.borrowerStats.avgCheckoutDuration} days helps plan inventory availability</div>
                <div>• {analytics.borrowerStats.totalBorrowers} active borrowing organizations create a diverse network</div>
              </div>
            </div>
          </div>
        )}

        {/* WAREHOUSE PARTNERS TAB */}
        {activeTab === 'warehouses' && (
          <div className="space-y-6">
            {/* Partnership Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <Building className="h-8 w-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{analytics.warehouseStats.totalPartners}</div>
                    <div className="text-sm text-gray-600">Partner Warehouses</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold text-green-600">{analytics.warehouseStats.lendingPartners.length}</div>
                    <div className="text-sm text-gray-600">Active Lending Partners</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3">
                  <MapPin className="h-8 w-8 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{analytics.warehouseStats.borrowingPartners.length}</div>
                    <div className="text-sm text-gray-600">Borrowing Partners</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Lending Partners (Organizations that borrow from us) */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  Organizations We Lend To Most ({timeRange})
                </h3>
              </div>
              <div className="p-4">
                {analytics.warehouseStats.lendingPartners.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.warehouseStats.lendingPartners.map((partner, index) => (
                      <div key={partner.organization} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium">{partner.organization}</div>
                            <div className="text-sm text-gray-600">Frequent borrower</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">{partner.loans}</div>
                          <div className="text-xs text-gray-500">loans</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No lending activity in this period</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Borrowing Partners (Organizations we borrow from) */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Organizations We Borrow From Most
                </h3>
              </div>
              <div className="p-4">
                {analytics.warehouseStats.borrowingPartners.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.warehouseStats.borrowingPartners.map((partner, index) => (
                      <div key={partner.organization} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium">{partner.organization}</div>
                            <div className="text-sm text-gray-600">Lending partner</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-blue-600">{partner.transfers}</div>
                          <div className="text-xs text-gray-500">transfers</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No borrowing activity recorded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Network Insights */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">🌐 Network Insights</h4>
              <div className="text-sm text-purple-800 space-y-1">
                <div>• You have {analytics.warehouseStats.totalPartners} partner warehouses in your network</div>
                <div>• {analytics.warehouseStats.lendingPartners.length} organizations actively borrow your props</div>
                <div>• {analytics.warehouseStats.borrowingPartners.length} organizations you regularly borrow from</div>
                <div>• Strong partnerships lead to better prop availability and cost savings</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ViewLayout>
  );
};

export default AnalyticsView;